import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, X, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

export default function ApprovalQueue() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/approvals/pending");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const decide = async (item, decision) => {
    setBusy(true);
    try {
      await api.post(`/approvals/${decision}`, {
        collection: item.collection,
        item_id: item.item.id,
        reason: decision === "reject" ? "Rejected via approval queue" : "",
      });
      toast.success(decision === "approve" ? "Approved" : "Rejected");
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="approval-queue-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">Approvals</div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Approval queue</h1>
        <p className="text-white/50 mt-2 text-sm">Anything clients submit for approval lands here. Approve or reject to keep the AI flow moving.</p>
      </header>

      {items.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center">
          <ClipboardCheck className="h-10 w-10 mx-auto text-white/30 mb-3" />
          <div className="text-white/50">No items pending approval — you're caught up.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="glass rounded-2xl p-6" data-testid={`approval-item-${it.item.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-mono">{it.label}</div>
                  <div className="text-xs text-white/50 mt-1">
                    From {it.owner?.name} · <span className="font-mono">{it.owner?.email}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => decide(it, "approve")}
                    data-testid={`approve-${it.item.id}`}
                    disabled={busy}
                    className="brand-gradient px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
                  >
                    <Check className="h-4 w-4" /> Approve
                  </button>
                  <button
                    onClick={() => decide(it, "reject")}
                    data-testid={`reject-${it.item.id}`}
                    disabled={busy}
                    className="glass px-4 py-2 rounded-lg text-sm hover:bg-white/5 inline-flex items-center gap-2"
                  >
                    <X className="h-4 w-4" /> Reject
                  </button>
                </div>
              </div>
              <pre className="text-xs text-white/70 whitespace-pre-wrap font-mono bg-white/[0.02] rounded-lg p-3 max-h-64 overflow-y-auto">
                {JSON.stringify(it.item, null, 2).slice(0, 1200)}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
